/**
 * <RecorderClock> — le temps d'un enregistrement, qui court tout seul.
 *
 * Il reçoit la durée figée au dernier changement (`durationMs`) et le début du segment en cours
 * (`runningSince`, en ms de `Date.now()`), et porte son propre décompte : c'est lui qui se redessine
 * chaque seconde, pas la feuille ni l'écran qui la pose (la page entière se redessinait chaque
 * seconde avec l'ancien enregistreur). Arrêté (`runningSince` absent), il ne tourne pas.
 *
 * « 00:42 », comme l'ancien enregistreur, et le plafond à côté, plus discret (« / 12:00 »).
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { useSecondClock } from "../../hooks/useSecondClock"
import { formatCountdown } from "../../../ports/primary/ui/formatters/dateFormatter"

export type RecorderClockProps = {
    durationMs: number
    runningSince?: number | null
    /** Le plafond, écrit à côté. Absent : rien. */
    maxDurationMs?: number
    testID?: string
}

export function RecorderClock(props: RecorderClockProps) {
    const { durationMs, runningSince, maxDurationMs, testID } = props
    // Les secondes se comptent depuis l'instant où l'enregistrement aurait valu zéro.
    const now = useSecondClock(runningSince != null ? runningSince - durationMs : null)

    const elapsed = durationMs + (runningSince != null ? Math.max(0, now - runningSince) : 0)
    const seconds = Math.floor(elapsed / 1000)

    return (
        <Row gap="2" align="end" justify="center" testID={testID}>
            <Text size="text-4xl" weight="semibold" color="text-strong" style={styles.digits}>
                {formatCountdown(seconds)}
            </Text>
            {maxDurationMs != null && (
                <Text size="text-md" color="text-muted" style={styles.max}>
                    {`/ ${formatCountdown(Math.floor(maxDurationMs / 1000))}`}
                </Text>
            )}
        </Row>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Les chiffres gardent leur largeur : le compteur ne tremble pas d'une seconde à l'autre. */
    digits: { fontVariant: ["tabular-nums"] },
    max: { fontVariant: ["tabular-nums"], paddingBottom: theme.spacing["1"] },
}))
